import { useMsal } from '@azure/msal-react';
import { MSAL_INTERACTION_STATUS_KEY, performLogout } from '@dispatch/web/auth';
import { Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import {
  BootstrapErrorButtonStyle,
  BootstrapErrorContainerStyle,
} from './BootstrapErrorStyles';

export interface BootstrapErrorProps {
  /**
   * Re-invokes the bootstrap queries (user/permissions) or triggers re-auth.
   * When omitted, falls back to a full page reload.
   */
  onRetry?: () => void;
}

/**
 * Actionable full-screen state shown when bootstrap data (user/permissions)
 * or a token refresh could not be obtained after recovery attempts were
 * exhausted. Replaces the previous indefinite spinner with a clear,
 * recoverable affordance (AC #4).
 */
export function BootstrapError({ onRetry }: BootstrapErrorProps) {
  const { instance } = useMsal();
  const [signingOut, setSigningOut] = useState(false);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  // Escape hatch (AC10): Retry reuses the (possibly dead) cached token, so
  // offer a deterministic re-auth via the hardened logout redirect. If the
  // redirect can't fire (wedged MSAL interaction), evict the lock and hard-
  // navigate so this backstop is never a silent no-op. `signingOut` disables
  // the button so a second click can't emit a spurious LogoutRedirectFailed.
  const handleSignOut = async (): Promise<void> => {
    setSigningOut(true);
    const redirected = await performLogout(instance);
    if (!redirected) {
      sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
      window.location.assign('/sign-in');
    }
  };

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={BootstrapErrorContainerStyle}
    >
      <Typography color="white" variant="h2" component="div">
        Something went wrong
      </Typography>
      <Typography color="white" variant="overline">
        We couldn&apos;t finish loading your account. Please try again.
      </Typography>
      <Button
        variant="outlined"
        onClick={handleRetry}
        sx={BootstrapErrorButtonStyle}
        data-testid="bootstrap-error-retry"
      >
        Retry
      </Button>
      <Button
        variant="outlined"
        onClick={() => void handleSignOut()}
        disabled={signingOut}
        sx={BootstrapErrorButtonStyle}
        data-testid="bootstrap-error-signout"
      >
        Sign out and try again
      </Button>
    </Stack>
  );
}

export default BootstrapError;
